import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as timeAdjustmentsApi from '../../api/timeAdjustments';

// Async thunks
export const fetchTimeAdjustmentsThunk = createAsyncThunk(
  'timeAdjustments/fetchAll',
  async ({ page = 1, limit = 20, type, agentId, dateFrom, dateTo }, { rejectWithValue }) => {
    try {
      return await timeAdjustmentsApi.fetchTimeAdjustments({ page, limit, type, agentId, dateFrom, dateTo });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch time adjustments');
    }
  }
);

export const fetchTimeAdjustmentThunk = createAsyncThunk(
  'timeAdjustments/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      return await timeAdjustmentsApi.fetchTimeAdjustment(id);
    } catch (error) {
      return rejectWithValue(error.message || `Failed to fetch time adjustment with ID ${id}`);
    }
  }
);

export const addTimeAdjustmentThunk = createAsyncThunk(
  'timeAdjustments/add',
  async (data, { rejectWithValue, dispatch, getState }) => {
    try {
      const result = await timeAdjustmentsApi.addTimeAdjustment(data);

      // Refresh the list after adding an item
      const { timeAdjustments } = getState();
      dispatch(fetchTimeAdjustmentsThunk({
        page: timeAdjustments.pagination.page,
        limit: timeAdjustments.pagination.limit,
        type: timeAdjustments.filter.type,
        agentId: timeAdjustments.filter.agentId,
        dateFrom: timeAdjustments.filter.dateFrom,
        dateTo: timeAdjustments.filter.dateTo
      }));

      return result;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add time adjustment');
    }
  }
);

export const updateTimeAdjustmentThunk = createAsyncThunk(
  'timeAdjustments/update',
  async (data, { rejectWithValue }) => {
    try {
      return await timeAdjustmentsApi.updateTimeAdjustment(data);
    } catch (error) {
      return rejectWithValue(error.message || `Failed to update time adjustment with ID ${data.id}`);
    }
  }
);

export const deleteTimeAdjustmentThunk = createAsyncThunk(
  'timeAdjustments/delete',
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const result = await timeAdjustmentsApi.deleteTimeAdjustment(id);

      const { timeAdjustments } = getState();
      // If we've removed the last item on a page, go to the previous page
      if (timeAdjustments.list.length === 1 && timeAdjustments.pagination.page > 1) {
        dispatch(fetchTimeAdjustmentsThunk({
          page: timeAdjustments.pagination.page - 1,
          limit: timeAdjustments.pagination.limit,
          type: timeAdjustments.filter.type,
          agentId: timeAdjustments.filter.agentId,
          dateFrom: timeAdjustments.filter.dateFrom,
          dateTo: timeAdjustments.filter.dateTo
        }));
      }

      return { id, result };
    } catch (error) {
      return rejectWithValue(error.message || `Failed to delete time adjustment with ID ${id}`);
    }
  }
);

export const fetchTimeAdjustmentsByAgentThunk = createAsyncThunk(
  'timeAdjustments/fetchByAgent',
  async ({ agentId, dateFrom, dateTo }, { rejectWithValue }) => {
    try {
      return await timeAdjustmentsApi.fetchTimeAdjustmentsByAgent(agentId, dateFrom, dateTo);
    } catch (error) {
      return rejectWithValue(error.message || `Failed to fetch time adjustments for agent ID ${agentId}`);
    }
  }
);

// Initial state
const initialState = {
  list: [],
  currentAdjustment: null,
  agentAdjustments: [],
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  },
  filter: {
    type: null,
    agentId: null,
    dateFrom: null,
    dateTo: null
  }
};

// Time adjustments slice
const timeAdjustmentsSlice = createSlice({
  name: 'timeAdjustments',
  initialState,
  reducers: {
    clearTimeAdjustmentErrors: (state) => {
      state.error = null;
    },
    setFilter: (state, action) => {
      state.filter = { ...state.filter, ...action.payload };
    },
    clearFilter: (state) => {
      state.filter = {
        type: null,
        agentId: null,
        dateFrom: null,
        dateTo: null
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all time adjustments
      .addCase(fetchTimeAdjustmentsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTimeAdjustmentsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.data || [];
        state.pagination = action.payload.pagination || {
          page: state.pagination.page,
          limit: state.pagination.limit,
          total: 0,
          pages: 0
        };
      })
      .addCase(fetchTimeAdjustmentsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch single time adjustment
      .addCase(fetchTimeAdjustmentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTimeAdjustmentThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAdjustment = action.payload;
      })
      .addCase(fetchTimeAdjustmentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add time adjustment
      .addCase(addTimeAdjustmentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTimeAdjustmentThunk.fulfilled, (state, action) => {
        state.loading = false;
        // The actual list update will happen when we refresh the list after adding
      })
      .addCase(addTimeAdjustmentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update time adjustment
      .addCase(updateTimeAdjustmentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTimeAdjustmentThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedItem = action.payload;

        // Update in the list if it exists
        state.list = state.list.map(item =>
          item.id === updatedItem.id ? { ...item, ...updatedItem } : item
        );

        // Update current adjustment if it's the one we're viewing
        if (state.currentAdjustment && state.currentAdjustment.id === updatedItem.id) {
          state.currentAdjustment = { ...state.currentAdjustment, ...updatedItem };
        }
      })
      .addCase(updateTimeAdjustmentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete time adjustment
      .addCase(deleteTimeAdjustmentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTimeAdjustmentThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(item => item.id !== action.payload.id);

        if (state.currentAdjustment && state.currentAdjustment.id === action.payload.id) {
          state.currentAdjustment = null;
        }
      })
      .addCase(deleteTimeAdjustmentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch time adjustments by agent
      .addCase(fetchTimeAdjustmentsByAgentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTimeAdjustmentsByAgentThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.agentAdjustments = action.payload;
      })
      .addCase(fetchTimeAdjustmentsByAgentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// Export actions
export const { clearTimeAdjustmentErrors, setFilter, clearFilter } = timeAdjustmentsSlice.actions;

// Selectors
export const selectAllTimeAdjustments = (state) => state.timeAdjustments.list;
export const selectCurrentTimeAdjustment = (state) => state.timeAdjustments.currentAdjustment;
export const selectAgentTimeAdjustments = (state) => state.timeAdjustments.agentAdjustments;
export const selectTimeAdjustmentsPagination = (state) => state.timeAdjustments.pagination;
export const selectTimeAdjustmentsLoading = (state) => state.timeAdjustments.loading;
export const selectTimeAdjustmentsError = (state) => state.timeAdjustments.error;
export const selectTimeAdjustmentsFilter = (state) => state.timeAdjustments.filter;

export default timeAdjustmentsSlice.reducer;
